import { Button, Typography } from '@components'
import { useCheckoutContext } from '@haus-storefront-react/checkout'
import { useOrderStates, useUpdateOrder } from '@haus-storefront-react/hooks'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import type { CheckoutData, PaymentMethodData, StepProps } from '../../types'
import { Checkbox } from '../../ui/checkbox/Checkbox'
import { Input } from '../../ui/input/Input'
import { OptionGroup } from '../../ui/option-group/OptionGroup'
import { OrderSummary } from './OrderSummary'
import { PaymentModal } from './PaymentModal'
import styles from './Payment.module.scss'
import { usePaymentMethods } from './use-payment-methods'

const MESSAGE_MAX_LENGTH = 2000

type PaymentMethodFormValues = {
  paymentMethod: PaymentMethodData['paymentMethod'] | null
  poNumber: string | null
  message: string
  acceptTerms: boolean
}

const PAYMENT_METHOD_STEP_INDEX = 1

const Payment = ({ index, activeOrder, portalContainer }: StepProps) => {
  const { data: checkoutData, setDataForStep } = useCheckoutContext<CheckoutData>()
  const { updateOrder, error: orderError } = useUpdateOrder()
  const { transitionOrderToState, error: transitionError } = useOrderStates()
  const { paymentMethods } = usePaymentMethods()
  const { t } = useTranslation()
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [modalPaymentMethod, setModalPaymentMethod] = useState<
    PaymentMethodData['paymentMethod'] | null
  >(null)
  const paymentMethodOptions = [
    { label: t('checkout.paymentMethod.options.invoice'), value: 'invoice' },
    { label: t('checkout.paymentMethod.options.creditCard'), value: 'stripe' },
  ]

  const genericPaymentErrorMessage = t('checkout.paymentMethod.error')
  const successUrl = `${window.location.origin}${t('urls.orderConfirmation')}?orderCode=${encodeURIComponent(activeOrder.code)}`

  const initialValues: PaymentMethodFormValues = {
    paymentMethod: checkoutData[PAYMENT_METHOD_STEP_INDEX]?.paymentMethod ?? null,
    poNumber: checkoutData[PAYMENT_METHOD_STEP_INDEX]?.poNumber ?? '',
    message: checkoutData[PAYMENT_METHOD_STEP_INDEX]?.message ?? '',
    acceptTerms: false,
  }

  const schema = Yup.object().shape({
    paymentMethod: Yup.string()
      .required(t('completeOrder.paymentMethodRequired'))
      .oneOf(paymentMethodOptions.map((option) => option.value)),
    poNumber: Yup.string(),
    message: Yup.string().max(
      MESSAGE_MAX_LENGTH,
      t('completeOrder.messageMaxLength', { maxCharacters: MESSAGE_MAX_LENGTH }),
    ),
    acceptTerms: Yup.boolean().oneOf([true], t('completeOrder.acceptTermsPrompt')),
  })

  const handleError = (_error: Error): string => {
    return genericPaymentErrorMessage
  }

  const errorMessage =
    paymentErrorMessage ??
    (orderError ? handleError(orderError) : transitionError ? handleError(transitionError) : null)

  const handleInvoicePayment = async (): Promise<void> => {
    const invoicePaymentMethod = paymentMethods?.find((method) => method.code === 'invoice')

    if (!invoicePaymentMethod) {
      setPaymentErrorMessage(genericPaymentErrorMessage)
      return
    }

    if (activeOrder.state === 'AddingItems') {
      const transitionedOrder = await transitionOrderToState('ArrangingPayment')

      if (!transitionedOrder) {
        setPaymentErrorMessage(genericPaymentErrorMessage)
        return
      }
    }

    await updateOrder({
      paymentMethodCode: invoicePaymentMethod.code,
    })

    if (orderError) {
      return
    }

    window.location.href = successUrl
  }

  const handleStripePayment = async (): Promise<void> => {
    if (activeOrder.state === 'AddingItems') {
      const transitionedOrder = await transitionOrderToState('ArrangingPayment')

      if (!transitionedOrder) {
        setPaymentErrorMessage(genericPaymentErrorMessage)
        return
      }
    }

    setModalPaymentMethod('stripe')
    setPaymentModalOpen(true)
  }

  const handlePaymentModalOpenChange = (open: boolean): void => {
    if (!open) {
      void transitionOrderToState('AddingItems')
      setModalPaymentMethod(null)
    }

    setPaymentModalOpen(open)
  }

  const onSubmit = async (values: PaymentMethodFormValues) => {
    if (!values.paymentMethod || !values.acceptTerms) return

    setPaymentErrorMessage(null)

    const data: PaymentMethodData = {
      paymentMethod: values.paymentMethod,
      poNumber: values.poNumber,
      message: values.message,
      acceptTerms: values.acceptTerms,
    }
    setDataForStep(data, index)

    try {
      const customFields: Record<string, string> = {}
      if (values.poNumber?.trim()) customFields.poNumber = values.poNumber.trim()
      if (values.message?.trim()) customFields.message = values.message.trim()

      if (Object.keys(customFields).length > 0) {
        const updatedOrder = await updateOrder({ customFields })

        if (!updatedOrder) {
          setPaymentErrorMessage(genericPaymentErrorMessage)
          return
        }
      }

      switch (data.paymentMethod) {
        case 'invoice':
          await handleInvoicePayment()
          break
        case 'stripe':
          await handleStripePayment()
          break
        default:
          break
      }
    } catch {
      setPaymentErrorMessage(genericPaymentErrorMessage)
    }
  }

  return (
    <div className={styles.paymentMethod}>
      <Typography variant="body" as="p">
        {t('checkout.paymentMethod.selectPaymentMethod')}{' '}
        <span className={styles.required} aria-hidden>
          *
        </span>
      </Typography>
      <Formik<PaymentMethodFormValues>
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {({ errors, touched }) => (
          <Form className={styles.form}>
            <OptionGroup
              name="paymentMethod"
              options={paymentMethodOptions}
              legend={t('a11y.completeOrder.aria.paymentMethodLabel')}
            />
            <Input
              errors={errors}
              touched={touched}
              name="poNumber"
              label={t('checkout.paymentMethod.purchaseOrder.poNumberLabel')}
            />
            <Input
              errors={errors}
              touched={touched}
              name="message"
              label={t('completeOrder.orderMessage')}
              textarea
              rows={4}
            />
            <OrderSummary activeOrder={activeOrder} />
            <Checkbox
              name="acceptTerms"
              label={
                <Trans
                  i18nKey="completeOrder.acceptTerms"
                  components={[
                    <span key="accept-terms-text" />,
                    <a
                      key="accept-terms-link"
                      href={t('urls.terms')}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('a11y.completeOrder.aria.termsOpenInNewTab')}
                    >
                      {t('checkout.paymentMethod.termsLinkText')}
                    </a>,
                  ]}
                />
              }
              errors={errors}
              touched={touched}
            />

            {errorMessage && (
              <div className={styles.error} role="alert">
                {errorMessage}
              </div>
            )}
            <div className={styles.buttonWrapper}>
              <Button type="submit">{t('checkout.paymentMethod.purchaseOrder.placeOrder')}</Button>
            </div>
          </Form>
        )}
      </Formik>
      <PaymentModal
        open={paymentModalOpen}
        onOpenChange={handlePaymentModalOpenChange}
        paymentMethod={modalPaymentMethod ?? undefined}
        portalContainer={portalContainer}
      />
    </div>
  )
}

export default Payment
